// Modo Pomodoro (P0.5). O ciclo fica só no aparelho (ASM-030); o tempo da tarefa
// continua no servidor — durante a pausa a tarefa fica pausada, então a pausa não conta.
import { formatClock } from './timer';

export const POMODORO_LIMITS = {
  /** foco em minutos: de 1 min a 12 h (ASM-035) */
  focus: { min: 1, max: 720 },
  /** descanso em SEGUNDOS: de 1 s a 24 h (ASM-033) */
  break: { min: 1, max: 86_400 },
} as const;

export const POMODORO_DEFAULTS = { focusMin: 25, breakSec: 300 } as const;

/** Atalhos de descanso no pop-up do Pomodoro (segundos): 5 min · 10 min · 15 min · 30 min. */
export const BREAK_SHORTCUTS = [300, 600, 900, 1800] as const;

/** Atalhos de foco no pop-up do Pomodoro (minutos): 25 min · 50 min (ASM-035). */
export const FOCUS_SHORTCUTS = [25, 50] as const;

/** block = a cada bloco de foco · task = ao finalizar cada tarefa */
export type BreakMode = 'block' | 'task';

/** ready = hora de relaxar: esperando o clique em "Vamos relaxar" (o descanso só começa com ele) */
export type PomodoroPhase = 'idle' | 'focus' | 'ready' | 'break' | 'breakOver';

export interface PomodoroState {
  phase: PomodoroPhase;
  taskId: string | null;
  /** fim da fase atual (ms), enquanto conta */
  endsAt: number | null;
  /** foco congelado por pausa manual: segundos restantes (ASM-031 — congela o bloco) */
  frozenRemaining: number | null;
  focusMin: number;
  /** duração do descanso em segundos */
  breakSec: number;
}

export type PomodoroEvent = 'focusEnded' | 'breakEnded' | null;

export function idlePomodoro(focusMin: number = POMODORO_DEFAULTS.focusMin, breakSec: number = POMODORO_DEFAULTS.breakSec): PomodoroState {
  return { phase: 'idle', taskId: null, endsAt: null, frozenRemaining: null, focusMin, breakSec };
}

/** Foco em minutos (1 min a 12 h); descanso em segundos (1 s a 24 h). */
export function validatePomodoroConfig(focusMin: number, breakSec: number): { focus?: true; break?: true } {
  const errors: { focus?: true; break?: true } = {};
  const ok = (v: number, r: { min: number; max: number }) => Number.isInteger(v) && v >= r.min && v <= r.max;
  if (!ok(focusMin, POMODORO_LIMITS.focus)) errors.focus = true;
  if (!ok(breakSec, POMODORO_LIMITS.break)) errors.break = true;
  return errors;
}

/** Novos minutos valem para os próximos blocos (AC-066). */
export function withConfig(state: PomodoroState, focusMin: number, breakSec: number): PomodoroState {
  return { ...state, focusMin, breakSec };
}

/**
 * Tarefa iniciada (ou retomada). Se o bloco estava congelado na mesma tarefa, ele
 * continua de onde parou; senão começa um bloco novo de foco.
 */
export function startFocus(state: PomodoroState, taskId: string, now: number): PomodoroState {
  if (state.phase === 'focus' && state.frozenRemaining != null && state.taskId === taskId) {
    return { ...state, endsAt: now + state.frozenRemaining * 1000, frozenRemaining: null };
  }
  if (state.phase === 'focus' && state.endsAt != null && state.taskId === taskId) return state;
  return {
    ...state,
    phase: 'focus',
    taskId,
    endsAt: now + state.focusMin * 60_000,
    frozenRemaining: null,
  };
}

/** Pausa manual no meio do foco: o bloco congela. */
export function freezeFocus(state: PomodoroState, now: number): PomodoroState {
  if (state.phase !== 'focus' || state.endsAt == null) return state;
  const remaining = Math.max(0, Math.ceil((state.endsAt - now) / 1000));
  return { ...state, endsAt: null, frozenRemaining: remaining };
}

/** Avança o relógio. Fim do foco → hora de relaxar; fim da pausa → "Voltar ao foco". */
export function tickPomodoro(state: PomodoroState, now: number): { state: PomodoroState; event: PomodoroEvent } {
  if (state.endsAt == null || now < state.endsAt) return { state, event: null };
  if (state.phase === 'focus') {
    return {
      state: { ...state, phase: 'ready', endsAt: null, frozenRemaining: null },
      event: 'focusEnded',
    };
  }
  if (state.phase === 'break') {
    return { state: { ...state, phase: 'breakOver', endsAt: null }, event: 'breakEnded' };
  }
  return { state, event: null };
}

/** Modo "ao finalizar cada tarefa": concluir a tarefa é a hora de relaxar (AC-069). */
export function readyToRelax(state: PomodoroState): PomodoroState {
  return { ...state, phase: 'ready', taskId: null, endsAt: null, frozenRemaining: null };
}

/** "Vamos relaxar": o descanso começa agora (a tarefa do bloco fica guardada para voltar a ela). */
export function startBreak(state: PomodoroState, now: number): PomodoroState {
  if (state.phase !== 'ready') return state;
  return { ...state, phase: 'break', endsAt: now + state.breakSec * 1000, frozenRemaining: null };
}

/** Pular a pausa (botão ⏭ na barra). */
export function skipBreak(state: PomodoroState): PomodoroState {
  if (state.phase !== 'break') return state;
  return { ...state, phase: 'breakOver', endsAt: null };
}

/** Desligar o Pomodoro: volta ao timer normal (o tempo da tarefa não é tocado). */
export function stopPomodoro(state: PomodoroState): PomodoroState {
  return idlePomodoro(state.focusMin, state.breakSec);
}

export interface PomodoroView {
  phase: PomodoroPhase;
  remaining: number;
  text: string;
}

export function pomodoroView(state: PomodoroState, now: number): PomodoroView {
  let remaining = 0;
  if (state.frozenRemaining != null) remaining = state.frozenRemaining;
  else if (state.endsAt != null) remaining = Math.max(0, Math.ceil((state.endsAt - now) / 1000));
  return { phase: state.phase, remaining, text: formatClock(remaining) };
}
